import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  ArrowLeft,
  Save,
  Truck,
  MapPin,
  Package,
  Clock,
  Weight,
  StickyNote,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────

type RecentAddresses = {
  destinations: Array<{
    address: string;
    city: string;
    state: string;
    zip: string;
    contact: string;
  }>;
  carriers: Array<{
    name: string;
    driver: string;
    truck_number: string;
    license: string;
    trailer: string;
  }>;
  customers: string[];
  clientTags?: string[];
};

type BolDetail = {
  id: number;
  bol_number: string;
  date: string;
  customer_name: string;
  reference_number: string;
  origin_name: string;
  origin_address: string;
  origin_city: string;
  origin_state: string;
  origin_zip: string;
  destination_address: string;
  destination_city: string;
  destination_state: string;
  destination_zip: string;
  destination_contact: string;
  material_type: string;
  material_description: string;
  load_type: string;
  client_tag: string;
  time_in: string;
  time_out: string;
  scale_operator: string;
  weight_gross: number | null;
  weight_tare: number | null;
  weight_net: number | null;
  weight_tons: number | null;
  carrier_name: string;
  driver_name: string;
  truck_number: string;
  license_plate: string;
  trailer_number: string;
  notes: string;
  status: string;
  work_order_id: number | null;
};

type WorkOrderOption = {
  id: number;
  wo_number: string;
  client_name: string | null;
  product_name: string | null;
  status: string;
};

type OpsClient = {
  id: number;
  name: string;
};

type OpsProduct = {
  id: number;
  product_name: string;
  product_id: string | null;
};

// ── Constants ──────────────────────────────────────────────────────────────────

const FALLBACK_MATERIAL_TYPES = IS_LOCAL_DEMO ? [
  "Compost", "Green Waste", "Wood Chips", "Food Scraps", "Mulch", "Topsoil", "Custom Blend", "Other",
] : [
  "Orchard Compost (Pistachio Blend)",
  "Orchard Compost (Citrus Blend)",
  "Compost",
  "Dairy Compost",
  "Biochar Blend",
  "Topsoil",
  "Potting Soil - Premium Mix",
  "Worm Castings",
  "Mulch - Hardwood",
  "Biosolids",
  "Food Waste",
  "Green Waste",
  "Manure",
  "Wood Chips",
  "Palletized Product",
  "Bagged Product",
  "Equipment/Items",
  "Custom Blend",
  "Other",
];

const LOAD_TYPES = ["Outbound", "Inbound", "Transfer", "Return"];

const ORIGIN_LOCATIONS = IS_LOCAL_DEMO ? [
  { name: "Green Valley Demo Yard", address: "100 Demonstration Lane", city: "Congress", state: "AZ", zip: "85332" },
] : [
  {
    name: "Congress AZ Plant",
    address: "18980 Stanton Road",
    city: "Congress",
    state: "AZ",
    zip: "85332",
  },
  {
    name: "Phoenix AZ Facility",
    address: "1634 North 19th Avenue",
    city: "Phoenix",
    state: "AZ",
    zip: "85007",
  },
];

const CLIENT_TAGS = IS_LOCAL_DEMO ? [
  { value: "", label: "None" },
  { value: "Nursery deliveries", label: "Nursery deliveries" },
  { value: "Community garden", label: "Community garden" },
] : [
  { value: "", label: "None" },
  { value: "Vanguard Renewables", label: "Vanguard Renewables" },
  { value: "Willcox Pistachio Project", label: "Willcox Pistachio Project" },
  { value: "3LAG / Jack Mendoza", label: "3LAG / Jack Mendoza" },
  { value: "Flower of the Gods", label: "Flower of the Gods" },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcNet(gross: string, tare: string): { net: number | null; tons: number | null } {
  const g = parseFloat(gross);
  const t = parseFloat(tare);
  if (isNaN(g) || isNaN(t)) return { net: null, tons: null };
  const net = g - t;
  return { net, tons: parseFloat((net / 2000).toFixed(4)) };
}

// Shared input/select styles
const inputClass =
  "w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 transition-colors";

const labelClass = "block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5";

// ── Autocomplete Component ─────────────────────────────────────────────────────

function AutocompleteInput({
  value,
  onChange,
  suggestions,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder: string;
  label: string;
}) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!value.trim()) return suggestions.slice(0, 8);
    const q = value.toLowerCase();
    return suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 8);
  }, [value, suggestions]);

  const showDropdown = focused && filtered.length > 0;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        placeholder={placeholder}
        className={inputClass}
      />
      {showDropdown && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
          {filtered.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                onChange(item);
                setFocused(false);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors truncate"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function EditBol() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  // ── Form State ──

  const [loaded, setLoaded] = useState(false);
  const [bolNumber, setBolNumber] = useState("");
  const [date, setDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  // Origin
  const [originName, setOriginName] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [originCity, setOriginCity] = useState("");
  const [originState, setOriginState] = useState("");
  const [originZip, setOriginZip] = useState("");

  // Destination
  const [destAddress, setDestAddress] = useState("");
  const [destCity, setDestCity] = useState("");
  const [destState, setDestState] = useState("");
  const [destZip, setDestZip] = useState("");
  const [destContact, setDestContact] = useState("");

  // Material
  const [materialType, setMaterialType] = useState("");
  const [loadType, setLoadType] = useState("");
  const [clientTag, setClientTag] = useState("");
  const [materialDescription, setMaterialDescription] = useState("");

  // Timing
  const [timeIn, setTimeIn] = useState("");
  const [timeOut, setTimeOut] = useState("");
  const [scaleOperator, setScaleOperator] = useState("");

  // Weight
  const [weightGross, setWeightGross] = useState("");
  const [weightTare, setWeightTare] = useState("");

  // Carrier
  const [carrierName, setCarrierName] = useState("");
  const [driverName, setDriverName] = useState("");
  const [truckNumber, setTruckNumber] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");

  // Notes
  const [notes, setNotes] = useState("");

  // Work Order
  const [workOrderId, setWorkOrderId] = useState<string>("");

  // ── Derived ──

  const { net: weightNet, tons: weightTons } = calcNet(weightGross, weightTare);

  // ── Fetch existing BOL ──

  const { data: bolData, isLoading: bolLoading, error: bolError } = useQuery<BolDetail>({
    queryKey: ["bol-detail", id],
    queryFn: async () => {
      const res = await apiFetch(`/api/ops/bols/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load BOL (${res.status})`);
      const data = await res.json();
      return data.bol || data;
    },
    enabled: !!token && !!id,
  });

  // Populate form when BOL data arrives
  useEffect(() => {
    if (!bolData || loaded) return;
    const b = bolData;
    setBolNumber(b.bol_number || "");
    setDate(b.date ? b.date.slice(0, 10) : "");
    setCustomerName(b.customer_name || "");
    setReferenceNumber(b.reference_number || "");
    setOriginName((IS_LOCAL_DEMO ? (b as any).origin_location : null) || b.origin_name || "");
    setOriginAddress(b.origin_address || "");
    setOriginCity(b.origin_city || "");
    setOriginState(b.origin_state || "");
    setOriginZip(b.origin_zip || "");
    setDestAddress(b.destination_address || "");
    setDestCity(b.destination_city || "");
    setDestState(b.destination_state || "");
    setDestZip(b.destination_zip || "");
    setDestContact((IS_LOCAL_DEMO ? (b as any).onsite_contact_name : null) || b.destination_contact || "");
    setMaterialType(b.material_type || "");
    setLoadType(b.load_type || LOAD_TYPES[0]);
    setClientTag(b.client_tag || "");
    setMaterialDescription(b.material_description || "");
    setTimeIn(b.time_in || "");
    setTimeOut(b.time_out || "");
    setScaleOperator((IS_LOCAL_DEMO ? (b as any).scale_operator_initials : null) || b.scale_operator || "");
    setWeightGross(IS_LOCAL_DEMO && (b as any).gross_weight != null ? String((b as any).gross_weight) : b.weight_gross != null ? String(b.weight_gross) : "");
    setWeightTare(IS_LOCAL_DEMO && (b as any).tare_weight != null ? String((b as any).tare_weight) : b.weight_tare != null ? String(b.weight_tare) : "");
    setCarrierName(b.carrier_name || "");
    setDriverName(b.driver_name || "");
    setTruckNumber(b.truck_number || "");
    setLicensePlate(b.license_plate || "");
    setTrailerNumber(b.trailer_number || "");
    setNotes(b.notes || "");
    setWorkOrderId(b.work_order_id ? String(b.work_order_id) : "");
    setLoaded(true);
  }, [bolData, loaded]);

  // ── Fetch recent addresses for autocomplete ──

  const { data: recentData } = useQuery<RecentAddresses>({
    queryKey: ["bol-recent-addresses"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/bols/recent-addresses", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { destinations: [], carriers: [], customers: [] };
      return res.json();
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  // ── Fetch work orders for dropdown ──

  const { data: workOrders } = useQuery<WorkOrderOption[]>({
    queryKey: ["work-orders-dropdown"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/work-orders", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.workOrders || data || []).map((w: any) => ({
        id: w.id,
        wo_number: w.wo_number,
        client_name: w.client_name,
        product_name: w.product_name,
        status: w.status,
      }));
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: clients = [] } = useQuery<OpsClient[]>({
    queryKey: ["ops-clients-for-bol"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/clients", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.clients ?? data ?? [];
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: products = [] } = useQuery<OpsProduct[]>({
    queryKey: ["ops-products-for-bol"],
    queryFn: async () => {
      const res = await apiFetch("/api/ops/products", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.products ?? data ?? [];
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const materialTypes = useMemo(() => {
    const productNames = products.map((p) => p.product_name).filter(Boolean);
    return [...new Set([...productNames, ...FALLBACK_MATERIAL_TYPES, ...(materialType ? [materialType] : [])])].sort((a, b) => a.localeCompare(b));
  }, [materialType, products]);

  const customerSuggestions = useMemo(
    () => [...new Set([...(clients.map((c) => c.name).filter(Boolean)), ...(recentData?.customers ?? [])])].sort(),
    [clients, recentData]
  );
  const carrierSuggestions = useMemo(
    () => [...new Set((recentData?.carriers ?? []).map((c) => c.name).filter(Boolean))],
    [recentData]
  );

  const clientTagOptions = useMemo(() => {
    const values = [
      ...CLIENT_TAGS.map((ct) => ct.value).filter(Boolean),
      ...clients.map((c) => c.name).filter(Boolean),
      ...(recentData?.clientTags ?? []),
      ...(clientTag ? [clientTag] : []),
    ];
    return [
      { value: "", label: "None" },
      ...[...new Set(values)].sort().map((value) => ({ value, label: value })),
    ];
  }, [clients, recentData, clientTag]);

  // ── Submit ──

  const submitMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        date,
        customerName,
        clientId: clients.find((client) => client.name.trim().toLowerCase() === customerName.trim().toLowerCase())?.id ?? null,
        referenceNumber: referenceNumber || null,
        originLocation: originName,
        originAddress,
        originCity,
        originState,
        originZip,
        destinationAddress: destAddress,
        destinationCity: destCity,
        destinationState: destState,
        destinationZip: destZip,
        onsiteContactName: destContact || null,
        materialType,
        materialDescription: materialDescription || null,
        loadType,
        clientTag: clientTag || null,
        timeIn: timeIn || null,
        timeOut: timeOut || null,
        scaleOperatorInitials: scaleOperator || null,
        grossWeight: weightGross ? parseFloat(weightGross) : null,
        tareWeight: weightTare ? parseFloat(weightTare) : null,
        netWeight: weightNet,
        netWeightTons: weightTons,
        carrierName: carrierName || null,
        driverName: driverName || null,
        truckNumber: truckNumber || null,
        licensePlate: licensePlate || null,
        trailerNumber: trailerNumber || null,
        notes: notes || null,
        workOrderId: workOrderId ? parseInt(workOrderId) : null,
      };

      const res = await apiFetch(`/api/ops/bols/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to update BOL (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      navigate(`/app/ops/bols/${id}`);
    },
  });

  // ── Origin quick select ──

  function selectOrigin(index: number) {
    const loc = ORIGIN_LOCATIONS[index];
    setOriginName(loc.name);
    setOriginAddress(loc.address);
    setOriginCity(loc.city);
    setOriginState(loc.state);
    setOriginZip(loc.zip);
  }

  // ── Destination autocomplete ──

  function selectDestination(dest: RecentAddresses["destinations"][0]) {
    setDestAddress(dest.address);
    setDestCity(dest.city);
    setDestState(dest.state);
    setDestZip(dest.zip);
    setDestContact(dest.contact);
  }

  // ── Carrier autocomplete ──

  function selectCarrier(carrier: RecentAddresses["carriers"][0]) {
    setCarrierName(carrier.name);
    setDriverName(carrier.driver);
    setTruckNumber(carrier.truck_number);
    setLicensePlate(carrier.license);
    setTrailerNumber(carrier.trailer);
  }

  // ── Loading state ──

  if (bolLoading) {
    return (
      <div className="max-w-6xl space-y-6">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-card border border-border rounded-xl p-5 animate-pulse"
            >
              <div className="h-4 bg-muted rounded w-24 mb-4" />
              <div className="space-y-3">
                <div className="h-10 bg-muted rounded w-full" />
                <div className="h-10 bg-muted rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ──

  if (bolError) {
    return (
      <div className="max-w-6xl space-y-6">
        <Link
          to="/app/ops"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Operations
        </Link>
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <AlertTriangle size={32} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-red-600 dark:text-red-400">
            {bolError instanceof Error ? bolError.message : "Failed to load BOL"}
          </p>
        </div>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/app/ops/bols/${id}`)}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="heading-display text-2xl text-foreground">
            Edit BOL #{bolNumber}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Update the shipping document details
          </p>
        </div>
      </div>

      {/* Error */}
      {submitMutation.isError && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">
            {submitMutation.error instanceof Error ? submitMutation.error.message : "Failed to update BOL"}
          </p>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitMutation.mutate();
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Left Column ── */}
          <div className="space-y-6">
            {/* Delivery Info */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Package size={16} className="text-forest-600 dark:text-forest-400" />
                Delivery Info
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Date *</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Reference #</label>
                  <input
                    type="text"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="PO or reference"
                    className={inputClass}
                  />
                </div>
              </div>

              <AutocompleteInput
                label="Customer Name *"
                value={customerName}
                onChange={setCustomerName}
                suggestions={customerSuggestions}
                placeholder={IS_LOCAL_DEMO ? "e.g. Demo Nursery" : "e.g. Vanguard Renewables"}
              />

              <div>
                <label className={labelClass}>Job / Work Order</label>
                <select
                  value={workOrderId}
                  onChange={(e) => setWorkOrderId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">No job assigned</option>
                  {(workOrders || []).map((wo) => (
                    <option key={wo.id} value={wo.id}>
                      {wo.wo_number} - {wo.client_name || wo.product_name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* Origin */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <MapPin size={16} className="text-forest-600 dark:text-forest-400" />
                Origin
              </div>

              <div className="flex flex-wrap gap-2">
                {ORIGIN_LOCATIONS.map((loc, i) => (
                  <button
                    key={loc.name}
                    type="button"
                    onClick={() => selectOrigin(i)}
                    className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                      originName === loc.name
                        ? "bg-forest-600 text-white border-forest-600"
                        : "bg-background text-muted-foreground border-border hover:border-forest-300 hover:text-foreground"
                    }`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelClass}>Address</label>
                  <input
                    type="text"
                    value={originAddress}
                    onChange={(e) => setOriginAddress(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    value={originCity}
                    onChange={(e) => setOriginCity(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>State</label>
                    <input
                      type="text"
                      value={originState}
                      onChange={(e) => setOriginState(e.target.value)}
                      className={inputClass}
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Zip</label>
                    <input
                      type="text"
                      value={originZip}
                      onChange={(e) => setOriginZip(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Destination */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <MapPin size={16} className="text-blue-600 dark:text-blue-400" />
                  Destination
                </div>

                {/* Recent destinations dropdown */}
                {(recentData?.destinations?.length ?? 0) > 0 && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-lg transition-colors"
                    >
                      Recent
                      <ChevronDown size={12} />
                    </button>
                    <div className="hidden group-focus-within:block absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1 min-w-[240px]">
                      {recentData!.destinations.map((dest, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectDestination(dest)}
                          className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                        >
                          <p className="font-medium truncate">{dest.address}</p>
                          <p className="text-xs text-muted-foreground">
                            {[dest.city, dest.state, dest.zip].filter(Boolean).join(", ")}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={destAddress}
                  onChange={(e) => setDestAddress(e.target.value)}
                  placeholder="Street address"
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3">
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    value={destCity}
                    onChange={(e) => setDestCity(e.target.value)}
                    placeholder="City"
                    className={inputClass}
                  />
                </div>
                <div className="col-span-1">
                  <label className={labelClass}>State</label>
                  <input
                    type="text"
                    value={destState}
                    onChange={(e) => setDestState(e.target.value)}
                    placeholder="AZ"
                    className={inputClass}
                    maxLength={2}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Zip</label>
                  <input
                    type="text"
                    value={destZip}
                    onChange={(e) => setDestZip(e.target.value)}
                    placeholder="85001"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Contact Person</label>
                <input
                  type="text"
                  value={destContact}
                  onChange={(e) => setDestContact(e.target.value)}
                  placeholder="Name or phone"
                  className={inputClass}
                />
              </div>
            </section>

            {/* Material */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Package size={16} className="text-amber-600 dark:text-amber-400" />
                Material
              </div>

              <div>
                <label className={labelClass}>Material Type *</label>
                <select
                  value={materialType}
                  onChange={(e) => setMaterialType(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">Select type...</option>
                  {materialTypes.map((mt) => (
                    <option key={mt} value={mt}>
                      {mt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Load Type *</label>
                  <select
                    value={loadType}
                    onChange={(e) => setLoadType(e.target.value)}
                    className={inputClass}
                    required
                  >
                    {LOAD_TYPES.map((lt) => (
                      <option key={lt} value={lt}>
                        {lt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Client Tag</label>
                  <select
                    value={clientTag}
                    onChange={(e) => setClientTag(e.target.value)}
                    className={inputClass}
                  >
                    {clientTagOptions.map((ct) => (
                      <option key={ct.value} value={ct.value}>
                        {ct.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  value={materialDescription}
                  onChange={(e) => setMaterialDescription(e.target.value)}
                  placeholder="Additional material details..."
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </section>
          </div>

          {/* ── Right Column ── */}
          <div className="space-y-6">
            {/* Timing */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Clock size={16} className="text-forest-600 dark:text-forest-400" />
                Timing
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Time In</label>
                  <input
                    type="time"
                    value={timeIn}
                    onChange={(e) => setTimeIn(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Time Out</label>
                  <input
                    type="time"
                    value={timeOut}
                    onChange={(e) => setTimeOut(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Scale Operator</label>
                <input
                  type="text"
                  value={scaleOperator}
                  onChange={(e) => setScaleOperator(e.target.value)}
                  placeholder="Operator name"
                  className={inputClass}
                />
              </div>
            </section>

            {/* Weight */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Weight size={16} className="text-amber-600 dark:text-amber-400" />
                Weight
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Gross (lbs)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={weightGross}
                    onChange={(e) => setWeightGross(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Tare (lbs)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={weightTare}
                    onChange={(e) => setWeightTare(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Auto-calculated fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    Net (lbs)
                  </p>
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {weightNet !== null ? weightNet.toLocaleString() : "--"}
                  </p>
                </div>
                <div className="bg-forest-50 dark:bg-forest-950/30 rounded-lg p-3 border border-forest-200 dark:border-forest-900">
                  <p className="text-xs font-semibold uppercase tracking-wider text-forest-700 dark:text-forest-400 mb-1">
                    Tons
                  </p>
                  <p className="text-lg font-bold text-forest-700 dark:text-forest-400 tabular-nums">
                    {weightTons !== null ? weightTons.toFixed(2) : "--"}
                  </p>
                </div>
              </div>
            </section>

            {/* Carrier */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                  <Truck size={16} className="text-blue-600 dark:text-blue-400" />
                  Carrier
                </div>

                {/* Recent carriers dropdown */}
                {(recentData?.carriers?.length ?? 0) > 0 && (
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-lg transition-colors"
                    >
                      Recent
                      <ChevronDown size={12} />
                    </button>
                    <div className="hidden group-focus-within:block absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto py-1 min-w-[200px]">
                      {recentData!.carriers.map((carrier, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectCarrier(carrier)}
                          className="block w-full text-left px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                        >
                          <p className="font-medium">{carrier.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {[carrier.driver, carrier.truck_number].filter(Boolean).join(" - ")}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <AutocompleteInput
                label="Carrier Name"
                value={carrierName}
                onChange={setCarrierName}
                suggestions={carrierSuggestions}
                placeholder="Trucking company"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Driver</label>
                  <input
                    type="text"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Driver name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Truck #</label>
                  <input
                    type="text"
                    value={truckNumber}
                    onChange={(e) => setTruckNumber(e.target.value)}
                    placeholder="Truck number"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>License Plate</label>
                  <input
                    type="text"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value)}
                    placeholder="License plate"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Trailer #</label>
                  <input
                    type="text"
                    value={trailerNumber}
                    onChange={(e) => setTrailerNumber(e.target.value)}
                    placeholder="Trailer number"
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            {/* Notes */}
            <section className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <StickyNote size={16} className="text-muted-foreground" />
                Notes
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes, instructions, or special handling..."
                rows={4}
                className={`${inputClass} resize-none`}
              />
            </section>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-8 pb-8">
          <button
            type="button"
            onClick={() => navigate(`/app/ops/bols/${id}`)}
            className="px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitMutation.isPending || !date || !customerName.trim()}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
          >
            <Save size={16} />
            {submitMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
